import axiosApiClient, { axiosApiPublicClient } from "..";
import { handleError } from "../../utils/errors";

const BASE_URL = "/model";

class ModelService {
  getCategories = async () => {
    try {
      const response = await axiosApiPublicClient.get(
        `/api${BASE_URL}/categories`
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching categories in");
    }
  };

  getCollections = async ({ page, search }) => {
    try {
      let url = `${BASE_URL}/collections?page=${page}`;
      if (search && search.trim() !== "") {
        url += `&search=${encodeURIComponent(search)}`;
      }

      const response = await axiosApiClient.get(url);
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching all collections");
    }
  };

  getUserFavorites = async ({ page, search }) => {
    try {
      let url = `${BASE_URL}/favorites/?page=${page}`;
      if (search && search.trim() !== "") {
        url += `&search=${encodeURIComponent(search)}`;
      }

      const response = await axiosApiClient.get(url);
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching user favorites");
    }
  };

  getExploreCollections = async ({ page, search, categoryId }) => {
    try {
      let url = `${BASE_URL}/explore/${categoryId}?page=${page}`;
      if (search && search.trim() !== "") {
        url += `&search=${encodeURIComponent(search)}`;
      }

      const response = await axiosApiClient.get(url);
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching explore collections"
      );
    }
  };

  generateCollection = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/generate-collection`,
        requestData
      );
      if (response.status !== 201) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while generating collection");
    }
  };

  regenerateCollection = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/regenerate-collection`,
        requestData
      );
      if (response.status !== 201) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while regenerating collection");
    }
  };

  toggleToFavorite = async ({ collectionId }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/favorite/${collectionId}`
      );
      if (response.status !== 201) {
        throw new Error();
      }

      return collectionId;
    } catch (err) {
      throw handleError(err, "Error occurred while toggle to favorite");
    }
  };

  deleteCollection = async (id) => {
    try {
      const response = await axiosApiClient.delete(
        `${BASE_URL}/collection/${id}`
      );
      if (response.status !== 200) {
        throw new Error();
      }
      response.data.id = id;
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while deleting collection");
    }
  };

  // Train model methods
  getTrainModels = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/train`);
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching train models");
    }
  };

  getTrainModel = async (trainModelId) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/train/${trainModelId}`
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching train model");
    }
  };

  createTrainModel = async ({ name }) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/train`, { name });
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while creating train model");
    }
  };

  updateTrainModel = async ({ trainModelId, formData }) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/train/${trainModelId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while updating train model");
    }
  };

  deleteTrainModel = async (trainModelId) => {
    try {
      const response = await axiosApiClient.delete(
        `${BASE_URL}/train/${trainModelId}`
      );
      if (response.status !== 200) {
        throw new Error();
      }
      response.data.id = trainModelId;
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while deleting train model");
    }
  };

  getTopCreators = async () => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/collection/top-creators`
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching top creators");
    }
  };
  rateModel = async ({ collectionId, rating }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/rate/${collectionId}`,
        { rating }
      );
      if (response.status !== 200) {
        throw new Error();
      }
      response.data.id = collectionId;
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while deleting collection");
    }
  };

  getDefaultGallery = async () => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/gallery?title=default`
      );
      if (response.status !== 200) {
        throw new Error();
      }

      const data = response.data.data.images;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching top creators");
    }
  };

  getUploadUrl = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/train/${requestData.trainModelId}/upload-url`,
        requestData
      );
      if (response.status !== 200) {
        throw new Error();
      }

      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching top creators");
    }
  };

  selectCoverForTrainModel = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/train/${requestData.trainModelId}/select-cover`,
        requestData
      );
      if (response.status !== 200) {
        throw new Error();
      }

      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while selecting cover");
    }
  };

  submitTrainModelForReview = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/train/${requestData.trainModelId}/submit-review`,
        requestData
      );
      if (response.status !== 200) {
        throw new Error();
      }

      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while submitting train model for review"
      );
    }
  };
}

const modelService = new ModelService();

export default modelService;
